import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabase/supabase';

const ProfileContext = createContext();

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

const DEFAULT_PROFILE = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  agencyName: 'Avoya Travel Network',
  tagline: ''
};

export const ProfileProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  const [usageCount, setUsageCount] = useState(0);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showProfilePanel, setShowProfilePanel] = useState(false);

  // Handle Auth State Changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        // Reset state on logout
        setProfile(DEFAULT_PROFILE);
        setUsageCount(0);
        setIsSubscribed(false);
        setIsLoaded(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId) => {
    try {
      let { data, error } = await supabase
        .from('profiles_99')
        .select('*')
        .eq('id', userId)
        .single();

      if (error && error.code === 'PGRST116') {
        // Profile doesn't exist yet, create it
        const { data: newProfile, error: insertError } = await supabase
          .from('profiles_99')
          .insert([{ id: userId, email: user?.email || '' }])
          .select()
          .single();

        if (insertError) throw insertError;
        data = newProfile;
      } else if (error) {
        throw error;
      }

      if (data) {
        setProfile({
          firstName: data.first_name || '',
          lastName: data.last_name || '',
          email: data.email || '',
          phone: data.phone || '',
          agencyName: data.agency_name || 'Avoya Travel Network',
          tagline: data.tagline || ''
        });
        setUsageCount(data.usage_count || 0);
        setIsSubscribed(data.is_subscribed || false);
      }
    } catch (e) {
      console.error("Error fetching profile:", e);
    } finally {
      setIsLoaded(true);
    }
  };

  const saveProfile = async (newProfile) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('profiles_99')
        .update({
          first_name: newProfile.firstName,
          last_name: newProfile.lastName,
          email: newProfile.email,
          phone: newProfile.phone,
          agency_name: newProfile.agencyName,
          tagline: newProfile.tagline,
          updated_at: new Date()
        })
        .eq('id', user.id);

      if (error) throw error;
      setProfile(newProfile);
    } catch (e) {
      console.error("Error saving profile:", e);
      throw e;
    }
  };

  const incrementUsage = async () => {
    if (!user) return;
    const newCount = usageCount + 1;
    try {
      const { error } = await supabase
        .from('profiles_99')
        .update({ usage_count: newCount })
        .eq('id', user.id);

      if (error) throw error;
      setUsageCount(newCount);
    } catch (e) {
      console.error("Error incrementing usage:", e);
    }
  };

  const activateSubscription = async () => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('profiles_99')
        .update({ is_subscribed: true })
        .eq('id', user.id);

      if (error) throw error;
      setIsSubscribed(true);
    } catch (e) {
      console.error("Error activating subscription:", e);
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (e) {
      console.error("Error signing out:", e);
    }
  };

  const isProfileComplete = profile.firstName && profile.lastName && profile.email;

  const getSignature = () => {
    if (!isProfileComplete) {
      return `\n\nWarm regards,\nYour Cruise Specialist\nAvoya Travel Network`;
    }
    return `\n\nWarm regards,\n${profile.firstName} ${profile.lastName}\n${profile.tagline || 'Cruise Specialist'}\n${profile.agencyName}\n${profile.email}${profile.phone ? ` | ${profile.phone}` : ''}`;
  };

  const isLimitReached = !isSubscribed && usageCount >= 10;

  return (
    <ProfileContext.Provider value={{
      user,
      profile,
      saveProfile,
      usageCount,
      incrementUsage,
      isSubscribed,
      activateSubscription,
      isLimitReached,
      isLoaded,
      isProfileComplete,
      getSignature,
      showProfilePanel,
      setShowProfilePanel,
      signOut
    }}>
      {children}
    </ProfileContext.Provider>
  );
};